import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Clock, BadgeCheck, Wallet } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { StatusBadge, PaymentBadge } from "@/components/StatusBadge";
import { format, parseISO } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { toDDMMYYYY } from "@/components/DatePicker";
import { api, errMsg, inr, fmtDay, getActivities, todayIST } from "@/lib/api";
import { ChangePassword } from "@/components/ChangePassword";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const STATUS_MSG = {
  pending: ["bg-amber-50 border-amber-200 text-amber-900", "Your application is under review. Your profile is NOT public until our team verifies your age and identity."],
  approved: ["bg-emerald-50 border-emerald-200 text-emerald-900", "Your profile is verified and live."],
  rejected: ["bg-rose-50 border-rose-200 text-rose-900", "Your application was not approved."],
  suspended: ["bg-rose-50 border-rose-200 text-rose-900", "Your profile is suspended and hidden from customers."],
};

const Requests = ({ onChange }) => {
  const [items, setItems] = useState(null);
  const [acts, setActs] = useState({});
  useEffect(() => {
    getActivities().then((a) => setActs(Object.fromEntries(a.map((x) => [x.slug, x.name_en])))).catch(() => {});
  }, []);
  const load = () => api.get("/companion/bookings").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);
  const act = async (b, action) => {
    try {
      await api.post(`/companion/bookings/${b.id}/${action}`, { reason: action === "cancel" ? "Companion unavailable" : null });
      toast.success(`Booking ${action}ed`);
      load();
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (items === null) return <Loader2 className="animate-spin" />;
  if (!items.length) return <p className="text-slate-500 py-8">No paid booking requests yet.</p>;
  return (
    <div className="space-y-3">
      {items.map((b) => (
        <div key={b.id} className="card-surface p-5 flex flex-col sm:flex-row gap-3 sm:items-center justify-between" data-testid={`companion-booking-${b.ref}`}>
          <div className="space-y-1">
            <div className="flex flex-wrap gap-2 items-center"><span className="font-mono text-sm font-semibold">{b.ref}</span><StatusBadge status={b.status} /><PaymentBadge status={b.payment_status} /></div>
            <p className="text-sm text-slate-700">{b.customer?.name} · {acts[b.activity] || b.activity} · {fmtDay(b.date)} {b.start_time} IST · {b.duration_hours}h</p>
            <p className="text-xs text-slate-500">Meet at: {b.meeting_place} · Your share (70%) {inr(b.pricing.companion_share)} · Cash: {b.payment_status}</p>
          </div>
          <div className="flex gap-2">
            {b.status === "pending" && <><Button size="sm" className="rounded-full" data-testid={`companion-accept-btn-${b.ref}`} onClick={() => act(b, "accept")}>Accept</Button><Button size="sm" variant="outline" className="rounded-full text-rose-600" data-testid={`companion-reject-btn-${b.ref}`} onClick={() => act(b, "reject")}>Reject</Button></>}
            {b.status === "confirmed" && new Date(b.start_at) <= new Date() && <Button size="sm" variant="outline" className="rounded-full" data-testid={`companion-complete-btn-${b.ref}`} onClick={() => act(b, "complete")}>Mark completed</Button>}
            {["accepted", "confirmed"].includes(b.status) && <Button size="sm" variant="ghost" className="rounded-full text-rose-600" data-testid={`companion-cancel-btn-${b.ref}`} onClick={() => act(b, "cancel")}>Cancel</Button>}
          </div>
        </div>
      ))}
    </div>
  );
};

const AvailabilityEditor = ({ me, onSaved }) => {
  const [week, setWeek] = useState(me.weekly_availability);
  const [blocked, setBlocked] = useState(me.blocked_dates);
  const setDay = (k, v) => setWeek({ ...week, [k]: v });
  const save = async () => {
    try {
      await api.put("/companion/me", { weekly_availability: week, blocked_dates: blocked });
      toast.success("Availability saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="card-surface p-6 space-y-4" data-testid="availability-editor">
      {DAYS.map((d, i) => {
        const k = String(i);
        const w = week[k];
        return (
          <div key={k} className="flex flex-wrap items-center gap-3 text-sm">
            <span className="w-28 font-semibold">{d}</span>
            <Switch data-testid={`avail-toggle-${i}`} checked={!!w} onCheckedChange={(v) => setDay(k, v ? { start: 10, end: 20 } : null)} />
            {w && (
              <>
                <Input type="number" min={6} max={23} className="w-20" value={w.start} onChange={(e) => setDay(k, { ...w, start: Number(e.target.value) })} data-testid={`avail-start-${i}`} />
                <span>to</span>
                <Input type="number" min={7} max={24} className="w-20" value={w.end} onChange={(e) => setDay(k, { ...w, end: Number(e.target.value) })} data-testid={`avail-end-${i}`} />
                <span className="text-slate-500 text-xs">hrs (IST)</span>
              </>
            )}
          </div>
        );
      })}
      <div className="space-y-2">
        <p className="text-sm font-semibold">Blocked dates (tap to add or remove)</p>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="rounded-xl border w-fit" data-testid="avail-blocked-calendar">
            <Calendar mode="multiple" selected={blocked.map((d) => parseISO(d))} disabled={{ before: parseISO(todayIST()) }}
              onSelect={(ds) => setBlocked((ds || []).map((d) => format(d, "yyyy-MM-dd")).sort())} />
          </div>
          <div className="flex flex-wrap content-start gap-2" data-testid="avail-blocked-list">
            {blocked.length === 0 && <p className="text-sm text-slate-500">No blocked dates.</p>}
            {blocked.map((d) => <span key={d} className="chip font-mono">{toDDMMYYYY(d)}<button type="button" className="ml-1.5 text-slate-400 hover:text-rose-600" onClick={() => setBlocked(blocked.filter((x) => x !== d))} data-testid={`avail-unblock-${d}`}>×</button></span>)}
          </div>
        </div>
      </div>
      <Button data-testid="avail-save-btn" className="rounded-full" onClick={save}>Save availability</Button>
    </div>
  );
};

const ProfileEditor = ({ me, onSaved }) => {
  const [bio, setBio] = useState(me.bio);
  const [rate, setRate] = useState(me.hourly_rate);
  const save = async () => {
    try {
      await api.put("/companion/me", { bio, hourly_rate: Number(rate) });
      toast.success("Profile saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="card-surface p-6 space-y-4" data-testid="profile-editor">
      <label className="block text-sm font-semibold space-y-1.5">Hourly rate (₹)<Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="profile-rate-input" /></label>
      <label className="block text-sm font-semibold space-y-1.5">Bio<Textarea rows={5} value={bio} onChange={(e) => setBio(e.target.value)} data-testid="profile-bio-input" /></label>
      <p className="text-xs text-slate-500">ID on file: {me.id_verification.doc_type} {me.id_verification.masked} · status {me.id_verification.status}</p>
      <Button className="rounded-full" onClick={save} data-testid="profile-save-btn">Save profile</Button>
    </div>
  );
};

export default function CompanionDashboard() {
  const tab = useSearchParams()[0].get("tab");
  const [me, setMe] = useState(null);
  const [earn, setEarn] = useState(null);
  const load = () => {
    api.get("/companion/me").then((r) => setMe(r.data));
    api.get("/companion/earnings").then((r) => setEarn(r.data));
  };
  useEffect(() => {
    load();
  }, []);
  if (!me) return <div className="py-24 grid place-items-center"><Loader2 className="animate-spin" /></div>;
  const [cls, msg] = STATUS_MSG[me.status];
  return (
    <div className="container-x py-10 space-y-6" data-testid="companion-dashboard">
      <div><p className="eyebrow">Companion Hub</p><h1 className="text-4xl sm:text-5xl font-bold mt-1">Hi, {me.display_name}</h1></div>
      <div className={`rounded-xl border p-4 text-sm ${cls}`} data-testid="companion-profile-status">
        <b className="uppercase font-mono text-xs tracking-wider mr-2">{me.status}</b>{msg}{me.review_note && ` Note: ${me.review_note}`}
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card-surface p-5"><Wallet className="text-[#0D5C46]" /><p className="text-xs text-slate-500 mt-2">Earnings (completed, 70% share)</p><p className="font-mono text-2xl font-semibold" data-testid="companion-earnings">{inr(earn?.earnings)}</p><p className="text-xs text-slate-500" data-testid="companion-payouts">Paid out {inr(earn?.paid_out)} · Payout pending {inr(earn?.payout_pending)}</p></div>
        <div className="card-surface p-5"><Clock className="text-[#0D5C46]" /><p className="text-xs text-slate-500 mt-2">Completed bookings</p><p className="font-mono text-2xl font-semibold">{earn?.completed_bookings ?? 0}</p></div>
        <div className="card-surface p-5"><BadgeCheck className="text-[#0D5C46]" /><p className="text-xs text-slate-500 mt-2">Rating</p><p className="font-mono text-2xl font-semibold">{me.rating?.toFixed(1)} <span className="text-sm text-slate-500">({me.review_count})</span></p></div>
      </div>
      <Tabs defaultValue={tab || "requests"} key={tab || "requests"}>
        <TabsList><TabsTrigger value="requests" data-testid="ctab-requests">Bookings</TabsTrigger><TabsTrigger value="availability" data-testid="ctab-availability">Availability</TabsTrigger><TabsTrigger value="profile" data-testid="ctab-profile">Profile</TabsTrigger><TabsTrigger value="security" data-testid="ctab-security">Password</TabsTrigger></TabsList>
        <TabsContent value="requests" className="mt-4"><Requests onChange={load} /></TabsContent>
        <TabsContent value="availability" className="mt-4"><AvailabilityEditor me={me} onSaved={load} /></TabsContent>
        <TabsContent value="profile" className="mt-4"><ProfileEditor me={me} onSaved={load} /></TabsContent>
        <TabsContent value="security" className="mt-4"><ChangePassword /></TabsContent>
      </Tabs>
    </div>
  );
}
